import CrudBusiness from "@/business/crud/CrudBusiness";
import Http from "@/commons/Http";
import FrontBusiness from "@/business/FrontBusiness";
import VueBusiness from "@/business/VueBusiness";
import Utils from "@/commons/Utils";

const path = 'api/papel';

const CLASSE_POR_RESTRICAO = {
  ONUS: 'GERAR_RESTRICAO_ONUS',
  ACAO: 'GERAR_RESTRICAO_ACAO',
  INDISPONIBILIDADE: 'GERAR_RESTRICAO_INDISPONIBILIDADE',
  COMPROMISSO: 'GERAR_RESTRICAO_COMPROMISSO_COMPRA_VENDA',
  DOMINIO: 'GERAR_RESTRICAO_DOMINIO_UTIL'
};

const PapelBusiness = {
  ...CrudBusiness(path),

  async refreshRootCache() {
    const papeis = await Http.get(`${path}/simple`).catch(() => []);
    const root = VueBusiness.getVueRoot();
    if (root) {
      root.papeis = Utils.orderBy(papeis.map(p => ({
        id: p.chave || p.id,
        nome: p.nome,
        dominios: p.dominios || [],
        classes: p.classes || []
      })), 'nome');
    }
  },

  async save(form, id = '') {
    if (id) {
      let vinculos = await Http.get(`${path}/verificar-vinculos/${id}/${false}`);

      if (vinculos > 0) {
        let confirmacao = await FrontBusiness.showConfirmacao(`Existem ${vinculos} vínculo(s) à esse papel.`, "Deseja continuar?");

        if (!confirmacao.value) {
          return;
        }
      }

      const result = await Http.post(`${path}/${id}`, form);
      await this.refreshRootCache();
      return result;
    }

    const result = await Http.post(`${path}`, form);
    await this.refreshRootCache();
    return result;
  },

  async delete(id) {
    const result = await Http.delete(`${path}/delete/${id}`).catch(e => FrontBusiness.showError('', e.message));
    if (result) {
      await this.refreshRootCache();
    }
    return result;
  },

  async findByDominio(dominio) {
    return Http.get(`${path}/dominio/${dominio}`);
  },

  getClasses() {
    return Http.get(`${path}/classe`);
  },

  getPapeisCensec() {
    return Http.get(`${path}/censec`);
  },

  getPapeisTjRj() {
    return Http.get(`${path}/tjrj`);
  },

  async cadastrarPadrao() {
    return Http.post(`${path}/cadastrar-padrao`);
  },

  // --- Cache de papéis ---

  getCachedPapeis() {
    return VueBusiness.getVueRoot()?.papeis || [];
  },

  getCachedPapeisByDominio(dominio) {
    let all = this.getCachedPapeis();
    if (!dominio) return all;

    if (dominio.startsWith('CERTIDAO_')) {
      dominio = dominio.replace('CERTIDAO_', 'PROTOCOLO_');
    }

    return all.filter(p => p.dominios?.includes(dominio));
  },

  getNomePapel(chave) {
    return this.getCachedPapeis().find(p => p.id === chave)?.nome || chave;
  },

  // --- Métodos de restrição (derivados das classes no cache) ---

  restricoesPorPapel(papel) {
    const papelCache = this.getCachedPapeis().find(p => p.id === papel);
    if (!papelCache) return [];
    return Object.entries(CLASSE_POR_RESTRICAO)
      .filter(([_, classe]) => papelCache.classes?.includes(classe))
      .map(([restricao]) => restricao);
  },

  papeisGeraRestricao() {
    const classesRestricao = Object.values(CLASSE_POR_RESTRICAO);
    return this.getCachedPapeis()
      .filter(p => p.classes?.some(c => classesRestricao.includes(c)))
      .map(p => p.id);
  },

  papeisPorRestricao(restricaoId) {
    const classe = CLASSE_POR_RESTRICAO[restricaoId];
    if (!classe) return [];
    return this.getCachedPapeis()
      .filter(p => p.classes?.includes(classe))
      .map(p => p.id);
  },

  restricoesPapeis() {
    const RESTRICOES = [
      {id: 'ONUS', nome: 'Ônus'},
      {id: 'ACAO', nome: 'Ação'},
      {id: 'COMPROMISSO', nome: 'Compromisso de Compra e Venda'},
      {id: 'DOMINIO', nome: 'Domínio Útil'}
    ];
    return RESTRICOES.map(r => ({
      id: r.id,
      nome: r.nome,
      habilitada: false,
      papeis: this.papeisPorRestricao(r.id).map(chave => ({
        id: chave,
        nome: this.getNomePapel(chave),
        habilitado: false
      }))
    }));
  }
};

export default PapelBusiness;
